import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { GridApi } from 'ag-grid-community'
import _ from 'lodash'
import DateVO from '../../../../../../../../../vo/DateVO'
import objects from '../../../../../../../../../utils/objects'
import { GanttCellRendererProps } from './index'
import { GanttColor } from '../../../../../../../../../styles/commonStyles'
import { WbsItemStatus } from '../../wbsItemStatus'
import {
  GanttDisplayUnit,
  GanttParameterVO,
} from '../../../../../../../../../domain/value-object/GanttParameterVO'
import { CalendarDateVO } from '../../../../../../../../../domain/value-object/CalendarDateVO'
import { colorPalette } from '../../../../../../../../style/colorPalette'
import { MilestoneDetail } from '../../../../../../../../../domain/entity/MilestoneEntity'
import {
  isGanttSummaryVisualType,
  getGanttBarHeight,
  GanttBarColorName,
  getBarColorByType,
  getShapeStyle,
  ACTUAL_BAR_GAP,
  ACTUAL_BAR_HEIGHT,
  resolveGanttBaseDate,
  type GanttBarVisualType,
} from '../ganttUtil'
import { GanttRowItem, ganttRowItemService } from '../GanttRowItem'
import { useDatePositions, DatePosition } from './hooks/useDatePositions'
export { useDatePositions }
export type { DatePosition }
import { DependencyLines } from './GanttDependency/DependencyLines'
import { CriticalPathLines } from './GanttCriticalPath/CriticalPathLines'
import { useGanttDependencyInteraction } from './GanttDependency/useGanttDependencyInteraction'
import {
  isOverSliderThumb,
  useGanttCriticalPathInteraction,
} from './GanttCriticalPath/useGanttCriticalPathInteraction'
import { useCellHoverEvents } from './hooks/useCellHoverEvents'
import { useBarCreationDrag } from './hooks/useBarCreationDrag'
import { LinkEditorPopup } from './WbsItemLink/LinkEditorPopup'
import { FallbackLinkIndicator } from './WbsItemLink/FallbackLinkIndicator'
import { WbsItemLinkDialog } from './WbsItemLink/WbsItemLinkDialog'
import { GanttColors, GanttDimensions } from './constants'
import { Tooltip as MuiTooltip, styled } from '@mui/material'
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined'
import { useIntl } from 'react-intl'
import { featureFlagService } from '../../../../../../../../../utils/feature-flag'

const isGanttImprovements = featureFlagService.isEnabled('gantt-improvements')

const Colors = {
  // Common
  GRID_LINE: '#d8dde5',
  GRID_LINE_LIGHT: colorPalette.monotone[2] + '66',
  TODAY_BACKGROUND: '#fdf3ea',
  TODAY_BORDER: GanttColor.BORDER_TODAY,
  HOLIDAY_BACKGROUND: '#d8dde526',
  // Legacy (v1)
  WARNING_BACKGROUND: '#fffacd',
  SCHEDULED: '#5bb9ff',
  SCHEDULED_BORDER: '#5bb9ff',
  LATE: '#f486b0',
  LATE_BORDER: '#f486b0',
  DONE: '#f7f8fa',
  DONE_BORDER: '#d8dde5',
  // V2
  ACTUAL_NORMAL: colorPalette.monotone[4],
  ACTUAL_NORMAL_BORDER: colorPalette.monotone[3],
  ACTUAL_LATE: colorPalette.pink[3],
  ACTUAL_LATE_BORDER: colorPalette.pink[4],
  ACTUAL_DONE: colorPalette.monotone[2],
  ACTUAL_DONE_BORDER: colorPalette.monotone[2],
} as const

interface Props extends GanttCellRendererProps {
  // Explicit rowIndex prop managed as state by CellRenderer.
  // node.rowIndex is mutable and invisible to React.memo, so this prop
  // ensures re-renders when row position changes after reorder.
  rowIndex?: number
  visualType: GanttBarVisualType
  rowItem?: GanttRowItem
}
export const GanttBackgroundCanvas = React.memo((props: Props) => {
  const {
    data,
    dataPath,
    timeScale,
    parameter,
    displayTimeScale,
    milestones,
    isV2,
    node,
    rowItem,
    getGanttRowItem,
  } = props

  // rowItem 経由でデータを取得（wbsItem 直接参照を排除）
  const status = rowItem?.status

  // 文字列日付 → Date への変換を useMemo で安定化し、毎レンダーの new Date() 生成を防止
  const scheduledDate = useMemo(
    () =>
      rowItem?.scheduledDate
        ? {
            startDate: rowItem.scheduledDate.startDate
              ? new Date(rowItem.scheduledDate.startDate)
              : undefined,
            endDate: rowItem.scheduledDate.endDate
              ? new Date(rowItem.scheduledDate.endDate)
              : undefined,
          }
        : undefined,
    [rowItem?.scheduledDate?.startDate, rowItem?.scheduledDate?.endDate]
  )
  const actualDate = useMemo(
    () =>
      rowItem?.actualDate
        ? {
            startDate: rowItem.actualDate.startDate
              ? new Date(rowItem.actualDate.startDate)
              : undefined,
            endDate: rowItem.actualDate.endDate
              ? new Date(rowItem.actualDate.endDate)
              : undefined,
          }
        : undefined,
    [rowItem?.actualDate?.startDate, rowItem?.actualDate?.endDate]
  )
  const maxScheduledEndDate = useMemo(
    () =>
      rowItem?.projectPlanCumulation?.maxScheduledEndDate
        ? new Date(rowItem.projectPlanCumulation.maxScheduledEndDate)
        : undefined,
    [rowItem?.projectPlanCumulation?.maxScheduledEndDate]
  )

  const hasValidData = !!rowItem
  const rowIndex = props.rowIndex ?? node?.rowIndex ?? -1

  const isProcess = ganttRowItemService.isProcess(rowItem)
  const isTask = ganttRowItemService.isTask(rowItem)
  const isDeliverable = ganttRowItemService.isDeliverable(rowItem)
  const wbsItemUuid = rowItem?.wbsItemUuid ?? null

  const progressLineEnabled = !!props.context?.progressLineEnabled
  const baseDate: Date | undefined = props.context?.ganttBaseDate

  const isRowItemSourced = !!getGanttRowItem
  const { updateRows, api: gridApi } = props
  const onGanttBarDateEdited = props.context?.onGanttBarDateEdited
  const dataRef = useRef(data)
  dataRef.current = data
  const nodeRef = useRef(props.node)
  nodeRef.current = props.node
  const commitBarCreation = useCallback(
    (startDate: string, endDate: string) => {
      if (isRowItemSourced && onGanttBarDateEdited) {
        onGanttBarDateEdited(dataRef.current, { startDate, endDate })
        return
      }
      const prefix = dataPath ? `${dataPath}.` : ''
      const cloned = _.cloneDeep(dataRef.current)
      objects.setValue(cloned, `${prefix}wbsItem.scheduledDate`, {
        startDate,
        endDate,
      })
      cloned.isEdited = true
      updateRows([cloned])
      setTimeout(() => {
        const node = nodeRef.current
        if (node) gridApi?.redrawRows({ rowNodes: [node] })
      })
    },
    [dataPath, updateRows, onGanttBarDateEdited, isRowItemSourced, gridApi]
  )

  if (!hasValidData) {
    return <div className="sevend-ag-cell-gantt-chart" />
  }

  return (
    <GanttChart
      status={status}
      scheduledDate={scheduledDate}
      actualDate={actualDate}
      maxScheduledEndDate={maxScheduledEndDate}
      timeScale={timeScale}
      displayTimeScale={displayTimeScale}
      unit={parameter.unit}
      milestones={milestones}
      isV2={isV2}
      visualType={props.visualType}
      rowIndex={rowIndex}
      isProcess={isProcess}
      isTask={isTask}
      isDeliverable={isDeliverable}
      wbsItemUuid={wbsItemUuid}
      api={props.api}
      dataPath={dataPath}
      progressLineEnabled={progressLineEnabled}
      baseDate={baseDate}
      getGanttRowItem={getGanttRowItem}
      rowItem={rowItem}
      commitBarCreation={commitBarCreation}
    />
  )
})

GanttBackgroundCanvas.displayName = 'GanttBackgroundCanvas'

type GanttChartProps = {
  status?: string
  scheduledDate?: { startDate?: Date; endDate?: Date }
  actualDate?: { startDate?: Date; endDate?: Date }
  maxScheduledEndDate?: Date
  timeScale: CalendarDateVO[]
  displayTimeScale: CalendarDateVO[]
  unit: GanttDisplayUnit
  milestones?: MilestoneDetail[]
  isV2?: boolean
  visualType: GanttBarVisualType
  rowIndex: number
  isProcess: boolean
  isTask: boolean
  isDeliverable: boolean
  wbsItemUuid: string | null
  api: GridApi
  dataPath?: string
  progressLineEnabled?: boolean
  baseDate?: Date
  getGanttRowItem?: (data: unknown) => GanttRowItem | undefined
  rowItem?: GanttRowItem
  commitBarCreation: (startDate: string, endDate: string) => void
}

const GanttChart = React.memo((props: GanttChartProps) => {
  const {
    status,
    scheduledDate,
    actualDate,
    maxScheduledEndDate,
    timeScale,
    displayTimeScale,
    unit,
    milestones,
    isV2,
    visualType,
    rowIndex,
    isProcess,
    isTask,
    isDeliverable,
    wbsItemUuid,
    api,
    dataPath,
    progressLineEnabled,
    baseDate,
    getGanttRowItem,
    rowItem,
    commitBarCreation,
  } = props

  const intl = useIntl()
  const datePositions = useDatePositions({ timeScale, unit })
  const linearDatePositions = useLinearDatePositions(timeScale)

  // Cell-level hover state and event delegation
  const {
    isHovered,
    isRowHovered,
    setIsRowHovered,
    chartContainerRef,
    onCellLeaveExtraRef,
    onCellMouseMoveRef,
    onCellMouseDownRef,
  } = useCellHoverEvents()

  const canCreateBar =
    isGanttImprovements &&
    !!rowItem?.editable &&
    !rowItem.scheduledDate?.startDate &&
    !rowItem.scheduledDate?.endDate

  const barCreation = useBarCreationDrag({
    timeScale,
    canCreate: canCreateBar,
    commitBarCreation,
    chartContainerRef,
  })

  const dateInfo = useMemo(() => {
    const now = resolveGanttBaseDate(baseDate)
    return {
      wbsIsDoneOrDiscard:
        status === WbsItemStatus.DONE || status === WbsItemStatus.DISCARD,
      wbsIsReview: status === WbsItemStatus.REVIEW,
      scheduledStart: scheduledDate?.startDate
        ? CalendarDateVO.of(scheduledDate.startDate)
        : undefined,
      scheduledEnd: scheduledDate?.endDate
        ? CalendarDateVO.of(scheduledDate.endDate)
        : undefined,
      actualStart: actualDate?.startDate
        ? CalendarDateVO.of(actualDate.startDate)
        : undefined,
      actualEnd: actualDate?.endDate
        ? CalendarDateVO.of(actualDate.endDate)
        : undefined,
      today: CalendarDateVO.of(now),
      endOfToday: CalendarDateVO.of(new DateVO(now).addDays(1).toDate()),
      maxScheduledDate: maxScheduledEndDate
        ? CalendarDateVO.of(maxScheduledEndDate)
        : undefined,
    }
  }, [
    status,
    scheduledDate?.startDate,
    scheduledDate?.endDate,
    actualDate?.startDate,
    actualDate?.endDate,
    maxScheduledEndDate,
    baseDate,
  ])

  // Bar endpoint positions (for connection circles and drop targets)
  const barEndpoints = useBarEndpoints(linearDatePositions, dateInfo)
  const depBarEndpoints = useDependencyBarEndpoints(
    barEndpoints,
    dateInfo,
    linearDatePositions
  )

  const backgrounds = useBackgrounds(datePositions, dateInfo, unit, isV2)
  const gridLines = useGridLines(datePositions, unit, displayTimeScale)
  const todayLine = useTodayLine(
    datePositions,
    dateInfo.today,
    progressLineEnabled
  )
  const bars = useBars(linearDatePositions, dateInfo, isV2, visualType)
  const milestoneLines = useMilestoneLines(datePositions, milestones)

  // Dependency interaction (context, categorization, drag, popup, fallback)
  const dep = useGanttDependencyInteraction({
    api,
    dataPath,
    rowIndex,
    isTask,
    isDeliverable,
    barEndpoints: depBarEndpoints,
    isHovered,
    isRowHovered,
  })

  // Critical path interaction (context, categorization, drag, popup, fallback)
  const cp = useGanttCriticalPathInteraction({
    api,
    dataPath,
    rowIndex,
    isProcess,
    wbsItemUuid,
    barEndpoints,
    datePositions,
    isHovered,
    isDependencyDragging: dep.isDependencyDragging,
    getGanttRowItem,
  })

  // Measure chart container dimensions for pixel-based line offsets
  const [chartWidth, setChartWidth] = useState(0)
  const [chartHeight, setChartHeight] = useState(0)
  useEffect(() => {
    const el = chartContainerRef.current
    if (!el) return
    const observer = new ResizeObserver(entries => {
      const entry = entries[0]
      if (entry) {
        setChartWidth(entry.contentRect.width)
        setChartHeight(entry.contentRect.height)
      }
    })
    observer.observe(el)
    setChartWidth(el.clientWidth)
    setChartHeight(el.clientHeight)
    return () => observer.disconnect()
  }, [chartContainerRef])

  // Wire up cell-level event callbacks from dep/cp/barCreation hooks
  onCellLeaveExtraRef.current = () => {
    cp.setIsCriticalPathRowHovered(false)
    cp.handleCellLeave()
    barCreation.handleCellLeave()
  }
  onCellMouseMoveRef.current = (_e, rect) => {
    cp.handleCellMouseMove(
      _e.clientX,
      _e.clientY,
      rect,
      PROCESS_BAR_HALF_HEIGHT,
      isOverSliderThumb(_e)
    )
    barCreation.handleCellMouseMove(_e, rect)
  }
  onCellMouseDownRef.current = (e, rect) => {
    cp.handleCellMouseDown(e, rect, PROCESS_BAR_HALF_HEIGHT, CIRCLE_SIZE)
    if (e.defaultPrevented) return
    barCreation.handleCellMouseDown(e, rect)
  }

  const {
    CONNECTION_CIRCLE_SIZE,
    CONNECTION_CIRCLE_OFFSET,
    DROP_TARGET_CIRCLE_SIZE,
  } = GanttDimensions

  // Symmetric offset: gap + radius = distance from bar edge to circle center
  const circleOffset = CONNECTION_CIRCLE_OFFSET + CONNECTION_CIRCLE_SIZE / 2
  const dropCircleOffset =
    CONNECTION_CIRCLE_OFFSET + DROP_TARGET_CIRCLE_SIZE / 2

  const isSummary = isGanttSummaryVisualType(visualType)

  return (
    <div
      ref={chartContainerRef}
      className="sevend-ag-cell-gantt-chart"
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'visible',
        pointerEvents: 'none',
      }}
    >
      <svg
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          overflow: 'visible',
          pointerEvents: 'none',
        }}
        preserveAspectRatio="none"
      >
        <g className="gantt-backgrounds">{backgrounds}</g>
        <g className="gantt-grid-lines">{gridLines}</g>
        <g className="gantt-today-line">{todayLine}</g>
        <g className="gantt-milestones">{milestoneLines}</g>
        <g className="gantt-bars">{bars}</g>
        <g className="gantt-dependency-lines" style={{ pointerEvents: 'auto' }}>
          <DependencyLines
            datePositions={linearDatePositions}
            dependencies={dep.dependencies}
            partialDependencies={dep.partialDeps}
            currentRowIndex={rowIndex}
            scheduledStart={dateInfo.scheduledStart}
            scheduledEnd={dateInfo.scheduledEnd}
            today={dateInfo.today}
            onLineClick={dep.handleDependencyLineClick}
            onPartialLineClick={dep.handleFallbackDepClick}
            containerWidth={chartWidth}
          />
        </g>
      </svg>
      {/* Critical path lines: separate SVG overlay above the slider */}
      <svg
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          overflow: 'visible',
          pointerEvents: 'none',
          zIndex: 3,
        }}
        preserveAspectRatio="none"
      >
        <g
          className="gantt-critical-path-lines"
          style={{ pointerEvents: 'auto' }}
        >
          <CriticalPathLines
            datePositions={datePositions}
            criticalPathLinks={cp.criticalPathLinks}
            partialLinks={cp.partialCPs}
            currentRowIndex={rowIndex}
            onLineClick={cp.handleCriticalPathLineClick}
            onPartialLineClick={cp.handleFallbackCpClick}
            barHalfHeight={PROCESS_BAR_HALF_HEIGHT}
            chartHeight={chartHeight}
          />
        </g>
      </svg>
      {/* Dependency: HTML drop target (above slider to receive mouse events) */}
      {dep.isPotentialDropTarget && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'auto',
            zIndex: 5,
          }}
          onMouseEnter={() => setIsRowHovered(true)}
          onMouseLeave={() => setIsRowHovered(false)}
          onMouseUp={dep.handleDropTargetMouseUp}
        />
      )}
      {/* Fallback endpoint icons for links with one hidden endpoint */}
      <FallbackLinkIndicator
        datePositions={datePositions}
        linearDatePositions={linearDatePositions}
        fallbackDeps={dep.fallbackDeps}
        fallbackCPs={cp.fallbackCPs}
        currentRowIndex={rowIndex}
        isSummaryRow={isSummary}
        onDepClick={dep.handleFallbackDepClick}
        onCpClick={cp.handleFallbackCpClick}
        onDepGroupClick={dep.handleFallbackDepGroupClick}
        onCpGroupClick={cp.handleFallbackCpGroupClick}
      />
      {/* Critical path: HTML drop target (above slider to receive mouse events) */}
      {cp.isCriticalPathDropTarget && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'auto',
            zIndex: 5,
          }}
          onMouseEnter={() => cp.setIsCriticalPathRowHovered(true)}
          onMouseLeave={() => {
            cp.setIsCriticalPathRowHovered(false)
            cp.criticalPathDragContext?.updateHoverDate(null)
          }}
          onMouseMove={cp.handleCriticalPathDropRowMouseMove}
          onMouseUp={cp.handleCriticalPathDropMouseUp}
        />
      )}
      {/* HTML-based dependency connection icons (above label z-index) */}
      {dep.showSourceCircles && depBarEndpoints.endX !== null && (
        <div
          className="gantt-drag-point-icon"
          style={{
            position: 'absolute',
            left: `calc(${depBarEndpoints.endX}% + ${circleOffset}px)`,
            top: '50%',
            transform: 'translate(-50%, -50%)',
            cursor: 'crosshair',
            pointerEvents: 'auto',
            zIndex: 5,
            lineHeight: 0,
          }}
          onMouseDown={e => dep.handleConnectionMouseDown(e, 'start')}
        >
          <AddCircleOutlineOutlinedIcon
            sx={{
              fontSize: CONNECTION_CIRCLE_SIZE,
              color: GanttColors.CONNECTION_CIRCLE,
            }}
          />
        </div>
      )}
      {/* HTML-based dependency drop target icons (above label z-index) */}
      {dep.showLeftDropTarget && depBarEndpoints.startX !== null && (
        <div
          style={{
            position: 'absolute',
            left: `calc(${depBarEndpoints.startX}% - ${dropCircleOffset}px)`,
            top: '50%',
            transform: 'translate(-50%, -50%)',
            cursor: 'pointer',
            pointerEvents: 'auto',
            zIndex: 5,
            lineHeight: 0,
          }}
          onMouseEnter={() => setIsRowHovered(true)}
          onMouseUp={dep.handleDropTargetMouseUp}
        >
          <AddCircleOutlineOutlinedIcon
            sx={{
              fontSize: DROP_TARGET_CIRCLE_SIZE,
              color: GanttColors.DROP_TARGET,
            }}
          />
        </div>
      )}
      {/* Critical path: drop point icon (HTML, above slider z-index) */}
      {cp.showCriticalPathDropHighlight &&
        cp.criticalPathDropPointPercent !== null &&
        (() => {
          const fromRow =
            cp.criticalPathDragContext?.dragState.fromRowIndex ?? -1
          const isFromAbove = fromRow < rowIndex
          return (
            <div
              style={{
                position: 'absolute',
                left: `${cp.criticalPathDropPointPercent}%`,
                top: isFromAbove
                  ? `calc(50% - ${PROCESS_BAR_HALF_HEIGHT}px)`
                  : `calc(50% + ${PROCESS_BAR_HALF_HEIGHT}px)`,
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
                zIndex: 15,
                lineHeight: 0,
              }}
            >
              <AddCircleOutlineOutlinedIcon
                sx={{
                  fontSize: DROP_TARGET_CIRCLE_SIZE,
                  color: GanttColors.DROP_TARGET,
                }}
              />
            </div>
          )
        })()}
      {/* Critical path: pre-drag indicator (HTML, above slider z-index) */}
      {cp.showCriticalPathPreDragCircle && cp.criticalPathPreDragInfo && (
        <CriticalPathPreDragIndicator
          className="gantt-drag-point-icon"
          pixelX={cp.criticalPathPreDragInfo.pixelX}
          position={cp.criticalPathPreDragInfo.position}
          label={cp.criticalPathPreDragInfo.date.format('M/D (ddd)')}
        />
      )}
      {/* Dependency line popup */}
      {dep.depPopup && (
        <LinkEditorPopup
          title={intl.formatMessage({ id: 'gantt.dependency' })}
          isCriticalPath={false}
          fromName={dep.depPopup.fromName}
          toName={dep.depPopup.toName}
          fromIconUrl={dep.depPopup.fromIconUrl}
          toIconUrl={dep.depPopup.toIconUrl}
          initialColor={dep.depPopup.dependency.color}
          initialDescription={dep.depPopup.dependency.description}
          onSave={dep.handleDependencyUpdate}
          onDelete={dep.handleDeleteDependency}
          onClose={dep.closeDependencyPopup}
        />
      )}
      {/* Critical path line popup */}
      {cp.cpPopup && (
        <LinkEditorPopup
          title={intl.formatMessage({
            id: 'gantt.criticalPath.interProcess',
          })}
          isCriticalPath={true}
          fromName={cp.cpPopup.fromName}
          toName={cp.cpPopup.toName}
          fromIconUrl={cp.cpPopup.fromIconUrl}
          toIconUrl={cp.cpPopup.toIconUrl}
          fromProjectName={cp.cpPopup.fromProjectName}
          toProjectName={cp.cpPopup.toProjectName}
          fromProjectIconUrl={cp.cpPopup.fromProjectIconUrl}
          toProjectIconUrl={cp.cpPopup.toProjectIconUrl}
          fromDate={cp.cpPopup.link.fromDate}
          toDate={cp.cpPopup.link.toDate}
          fromDateRange={cp.cpPopup.fromDateRange}
          toDateRange={cp.cpPopup.toDateRange}
          initialColor={cp.cpPopup.link.color}
          initialDescription={cp.cpPopup.link.description}
          onSave={cp.handleCriticalPathUpdate}
          onDelete={cp.handleDeleteCriticalPath}
          onClose={cp.closeCriticalPathPopup}
          readOnly={cp.cpPopup.link.editable === false}
        />
      )}
      {/* Dependency list dialog (grouped fallback icons) */}
      {dep.depListDialog && (
        <WbsItemLinkDialog
          open={true}
          onClose={dep.closeDepListDialog}
          type="dependency"
          items={dep.depListDialog.items}
          onUpdateLink={dep.handleDepDialogUpdate}
          onRemoveLink={dep.handleDepDialogRemove}
        />
      )}
      {/* Critical path list dialog (grouped fallback icons) */}
      {cp.cpListDialog && (
        <WbsItemLinkDialog
          open={true}
          onClose={cp.closeCpListDialog}
          type="criticalPath"
          items={cp.cpListDialog.items}
          onUpdateLink={cp.handleCpDialogUpdate}
          onRemoveLink={cp.handleCpDialogRemove}
        />
      )}
      {/* Bar creation: cursor guide (day-cell highlight + date tooltip) */}
      {barCreation.cursorGuide && (
        <BarCreationTooltip
          title={barCreation.cursorGuide.date.format('M/D (ddd)')}
        >
          <div
            style={{
              position: 'absolute',
              left: `${barCreation.cursorGuide.leftPercent}%`,
              top: 0,
              width: `${barCreation.cursorGuide.widthPercent}%`,
              height: '100%',
              backgroundColor: getBarColorByType(visualType, false),
              opacity: 0.12,
              pointerEvents: 'none',
              zIndex: 10,
            }}
          />
        </BarCreationTooltip>
      )}
      {/* Bar creation: preview bar + start/end date tooltips */}
      {barCreation.previewBar && (
        <>
          <BarCreationTooltip
            title={barCreation.previewBar.startDate.format('M/D (ddd)')}
          >
            <div
              style={{
                position: 'absolute',
                left: `${barCreation.previewBar.leftPercent}%`,
                top: '50%',
                width: `${100 / timeScale.length}%`,
                height: 0,
                pointerEvents: 'none',
                zIndex: 11,
              }}
            />
          </BarCreationTooltip>
          <BarCreationTooltip
            title={barCreation.previewBar.endDate.format('M/D (ddd)')}
          >
            <div
              style={{
                position: 'absolute',
                left: `${barCreation.previewBar.leftPercent + barCreation.previewBar.widthPercent - 100 / timeScale.length}%`,
                top: '50%',
                width: `${100 / timeScale.length}%`,
                height: 0,
                pointerEvents: 'none',
                zIndex: 11,
              }}
            />
          </BarCreationTooltip>
          <BarCreationPreview
            leftPercent={barCreation.previewBar.leftPercent}
            widthPercent={barCreation.previewBar.widthPercent}
            visualType={visualType}
            chartWidth={chartWidth}
            barColor={rowItem?.color}
          />
        </>
      )}
    </div>
  )
})

GanttChart.displayName = 'GanttChart'

const BarCreationTooltip: React.FC<{
  title: string
  children: React.ReactElement
}> = ({ title, children }) => (
  <MuiTooltip
    open={true}
    title={title}
    placement="top"
    arrow={true}
    disableHoverListener={true}
    disableFocusListener={true}
    disableTouchListener={true}
  >
    {children}
  </MuiTooltip>
)

const buildRoundedRectPath = (
  leftPx: number,
  rightPx: number,
  h: number,
  r: number,
  bottomRounded: boolean
): string => {
  const br = bottomRounded ? r : 0
  return `M ${leftPx + r} 0 H ${rightPx - r} A ${r} ${r} 0 0 1 ${rightPx} ${r} V ${h - br}${bottomRounded ? ` A ${r} ${r} 0 0 1 ${rightPx - r} ${h}` : ''} H ${leftPx + (bottomRounded ? r : 0)}${bottomRounded ? ` A ${r} ${r} 0 0 1 ${leftPx} ${h - r}` : ''} V ${r} A ${r} ${r} 0 0 1 ${leftPx + r} 0 Z`
}

const BarCreationPreview: React.FC<{
  leftPercent: number
  widthPercent: number
  visualType: GanttBarVisualType
  chartWidth: number
  barColor?: GanttBarColorName
}> = ({ leftPercent, widthPercent, visualType, chartWidth, barColor }) => {
  if (chartWidth <= 0) return null
  const h = getGanttBarHeight(visualType)
  const color = getBarColorByType(visualType, false, barColor)
  const shape = getShapeStyle(visualType)

  const leftPx = (leftPercent / 100) * chartWidth
  const barW = (widthPercent / 100) * chartWidth
  const rightPx = leftPx + barW

  const isArrow = shape === 'arrow'
  const hasTriangle = shape === 'line-triangle' || shape === 'bar-triangle'
  const isPill = shape === 'pill'
  const tabS = Math.min(h, 8, barW / 2)
  const showTriangles = hasTriangle && tabS >= 2
  const rMax =
    isPill || hasTriangle ? Math.min(h / 2, barW / 2) : Math.min(2, barW / 2)

  let barEl: React.ReactNode
  if (isArrow) {
    const tip = Math.min(barW * 0.15, 20)
    barEl = (
      <polygon
        points={`${leftPx},0 ${rightPx - tip},0 ${rightPx},${h / 2} ${rightPx - tip},${h} ${leftPx},${h}`}
        fill={color}
        opacity={0.8}
      />
    )
  } else {
    barEl = (
      <path
        d={buildRoundedRectPath(leftPx, rightPx, h, rMax, !hasTriangle)}
        fill={color}
        opacity={0.8}
      />
    )
  }

  return (
    <svg
      style={{
        position: 'absolute',
        top: '50%',
        transform: `translateY(-${h / 2}px)`,
        left: 0,
        width: '100%',
        height: h + (showTriangles ? tabS : 0),
        pointerEvents: 'none',
        zIndex: 10,
        overflow: 'visible',
      }}
    >
      {barEl}
      {showTriangles && (
        <>
          <polygon
            points={`${leftPx},${h - 0.5} ${leftPx + tabS},${h - 0.5} ${leftPx},${h - 0.5 + tabS}`}
            fill={color}
            opacity={0.8}
          />
          <polygon
            points={`${rightPx - tabS},${h - 0.5} ${rightPx},${h - 0.5} ${rightPx},${h - 0.5 + tabS}`}
            fill={color}
            opacity={0.8}
          />
        </>
      )}
    </svg>
  )
}

// Custom hooks

const useBarEndpoints = (
  datePositions: DatePosition[],
  dateInfo: {
    scheduledStart?: CalendarDateVO
    scheduledEnd?: CalendarDateVO
    endOfToday: CalendarDateVO
  }
): { startX: number | null; endX: number | null } => {
  return useMemo(() => {
    const { scheduledStart, scheduledEnd, endOfToday } = dateInfo

    if (!scheduledStart || datePositions.length === 0) {
      return { startX: null, endX: null }
    }

    const findPosition = (targetDate: CalendarDateVO) =>
      datePositions.find(p => p.date.isEqual(targetDate))

    const endDate = scheduledEnd || endOfToday
    const firstVisibleDate = datePositions[0].date
    const lastVisibleDate = datePositions[datePositions.length - 1].date

    // バーが表示範囲と完全に重ならない場合は null
    if (
      endDate.isBefore(firstVisibleDate) ||
      scheduledStart.isAfter(lastVisibleDate)
    ) {
      return { startX: null, endX: null }
    }

    const startPos = findPosition(scheduledStart)
    const endPos = findPosition(endDate)

    // 表示範囲外の端点はクランプ（左端 0 / 右端 100）
    const startX = startPos
      ? startPos.x
      : scheduledStart.isBefore(firstVisibleDate)
        ? 0
        : null
    const endX = endPos
      ? endPos.x + endPos.width
      : endDate.isAfter(lastVisibleDate)
        ? 100
        : null

    return { startX, endX }
  }, [datePositions, dateInfo])
}

/**
 * Dependency-specific bar endpoints.
 * Falls back to today's position when scheduledStart is absent,
 * so that drag-and-drop for dependencies works on unscheduled tasks.
 *
 * Positions are aligned with DependencyLines' resolveDependencyDates:
 * - startX = left edge of (scheduledStart ?? today)  … matches toStartDate fallback
 * - endX   = right edge of (scheduledEnd ?? today)   … matches fromEndDate fallback
 */
const useDependencyBarEndpoints = (
  barEndpoints: { startX: number | null; endX: number | null },
  dateInfo: {
    scheduledStart?: CalendarDateVO
    scheduledEnd?: CalendarDateVO
    today: CalendarDateVO
  },
  linearDatePositions: DatePosition[]
): { startX: number | null; endX: number | null } => {
  return useMemo(() => {
    if (barEndpoints.startX !== null && barEndpoints.endX !== null) {
      // scheduledEnd未設定時、useBarEndpointsはendOfTodayを使うが
      // DependencyLinesはtodayを使うため、endXをtodayの右端に補正する
      if (!dateInfo.scheduledEnd) {
        const todayPos = linearDatePositions.find(p =>
          p.date.isEqual(dateInfo.today)
        )
        if (todayPos) {
          return {
            startX: barEndpoints.startX,
            endX: todayPos.x + todayPos.width,
          }
        }
      }
      return barEndpoints
    }
    // scheduledStartがあるのにnullの場合 = 表示範囲外。フォールバック不要
    if (dateInfo.scheduledStart || linearDatePositions.length === 0) {
      return barEndpoints
    }
    // scheduledStart未設定 → フォールバック位置を計算
    const { scheduledEnd, today } = dateInfo
    const firstDate = linearDatePositions[0].date
    const lastDate = linearDatePositions[linearDatePositions.length - 1].date

    if (today.isBefore(firstDate) || today.isAfter(lastDate)) {
      return barEndpoints
    }

    const findPos = (d: CalendarDateVO) =>
      linearDatePositions.find(p => p.date.isEqual(d))

    const todayPos = findPos(today)
    if (!todayPos) return barEndpoints

    // startX: todayの左端（DependencyLinesの toStartDate = scheduledStart ?? today と一致）
    const startX = todayPos.x

    // endX: scheduledEndがあればその右端、なければtodayの右端
    // （DependencyLinesの fromEndDate = scheduledEnd ?? today と一致）
    let endX: number | null = null
    if (scheduledEnd) {
      const endPos = findPos(scheduledEnd)
      if (endPos) {
        endX = endPos.x + endPos.width
      } else if (scheduledEnd.isAfter(lastDate)) {
        endX = 100
      }
    }
    if (endX === null) {
      endX = todayPos.x + todayPos.width
    }

    return { startX, endX }
  }, [barEndpoints, dateInfo, linearDatePositions])
}

/** Slider-compatible linear positions (for bars that must align with MUI Slider) */
export const useLinearDatePositions = (
  timeScale: CalendarDateVO[]
): DatePosition[] => {
  return useMemo(() => {
    const width = 100 / timeScale.length
    return timeScale.map((date, i) => ({ x: i * width, width, date }))
  }, [timeScale])
}

/**
 * HTML-based pre-drag indicator for critical path.
 * Rendered as HTML (not SVG) so it appears above the GanttSlider.
 * Shows a circle with date tooltip at the bottom of the row.
 */
const CIRCLE_SIZE = 16

// Process bar height in v2 is 26px, centered vertically in the row.
// Half-height used to align the indicator with the bar edge.
const PROCESS_BAR_HALF_HEIGHT = 13

const CIRCLE_INNER_SIZE = 12

const PreDragIconContainer = styled('div')({
  position: 'relative',
  cursor: 'crosshair',
  lineHeight: 0,
})

const PreDragIconBackdrop = styled('div')({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: CIRCLE_INNER_SIZE,
  height: CIRCLE_INNER_SIZE,
  borderRadius: '50%',
  backgroundColor: colorPalette.monotone[0],
})

const CriticalPathPreDragIndicator: React.FC<{
  pixelX: number
  position: 'top' | 'bottom'
  label: string
  className?: string
}> = ({ pixelX, position, label, className }) => {
  const intl = useIntl()
  const isTop = position === 'top'
  const tooltipTitle = (
    <div style={{ textAlign: 'center' }}>
      <div>{intl.formatMessage({ id: 'gantt.criticalPath.interProcess' })}</div>
      <div>{label}</div>
    </div>
  )
  return (
    <div
      className={className}
      style={{
        position: 'absolute',
        left: pixelX,
        top: isTop
          ? `calc(50% - ${PROCESS_BAR_HALF_HEIGHT}px)`
          : `calc(50% + ${PROCESS_BAR_HALF_HEIGHT}px)`,
        transform: 'translate(-50%, -50%)',
        pointerEvents: 'none',
        zIndex: 10,
      }}
    >
      {/* Popper は body 直下に描画されるため、イベントを拾うとセルの mousemove が
          途切れて⊕が消える。特に下向きは次の行まで覆うので必ず透過させる */}
      <MuiTooltip
        open={true}
        title={tooltipTitle}
        placement={isTop ? 'top' : 'bottom'}
        arrow={true}
        disableInteractive={true}
        PopperProps={{ sx: { pointerEvents: 'none' } }}
      >
        <PreDragIconContainer>
          <PreDragIconBackdrop />
          <AddCircleOutlineOutlinedIcon
            sx={{
              fontSize: CIRCLE_SIZE,
              color: GanttColors.CRITICAL_PATH_CIRCLE,
              display: 'block',
              position: 'relative',
            }}
          />
        </PreDragIconContainer>
      </MuiTooltip>
    </div>
  )
}

const useBackgrounds = (
  datePositions: DatePosition[],
  dateInfo: {
    today: CalendarDateVO
    scheduledEnd?: CalendarDateVO
    maxScheduledDate?: CalendarDateVO
  },
  unit: GanttDisplayUnit,
  isV2?: boolean
): React.ReactNode[] => {
  return useMemo(() => {
    const { today, scheduledEnd, maxScheduledDate } = dateInfo
    const backgrounds: React.ReactNode[] = []

    for (let i = 0; i < datePositions.length; i++) {
      const { x, width, date } = datePositions[i]
      const isToday = date.isEqual(today)

      if (isToday) {
        backgrounds.push(
          <rect
            key={`today-${i}`}
            x={`${x}%`}
            y="0"
            width={`${width}%`}
            height="100%"
            fill={Colors.TODAY_BACKGROUND}
          />
        )
      } else if (unit === GanttDisplayUnit.DAY && date.isHoliday()) {
        backgrounds.push(
          <rect
            key={`holiday-${i}`}
            x={`${x}%`}
            y="0"
            width={`${width}%`}
            height="100%"
            fill={Colors.HOLIDAY_BACKGROUND}
          />
        )
      }

      // Legacy: warning background for overrun area
      if (
        !isV2 &&
        scheduledEnd &&
        maxScheduledDate &&
        date.isAfter(scheduledEnd) &&
        !date.isAfter(maxScheduledDate)
      ) {
        backgrounds.push(
          <rect
            key={`warning-${i}`}
            x={`${x}%`}
            y="25%"
            width={`${width}%`}
            height="50%"
            fill={Colors.WARNING_BACKGROUND}
          />
        )
      }
    }
    return backgrounds
  }, [datePositions, dateInfo, unit, isV2])
}

export const useOverrunPosition = (
  datePositions: DatePosition[],
  scheduledEndDate?: Date,
  maxScheduledEndDate?: Date
): { left: number; width: number } | null => {
  return useMemo(() => {
    if (
      !scheduledEndDate ||
      !maxScheduledEndDate ||
      datePositions.length === 0
    ) {
      return null
    }

    const scheduledEnd = CalendarDateVO.of(scheduledEndDate)
    const maxScheduledDate = CalendarDateVO.of(maxScheduledEndDate)

    const startPos = datePositions.find(p => p.date.isEqual(scheduledEnd))
    const endPos = datePositions.find(p => p.date.isEqual(maxScheduledDate))

    const firstDate = datePositions[0].date
    const lastDate = datePositions[datePositions.length - 1].date

    // Both out of range: only show if overrun spans the entire display range
    if (!startPos && !endPos) {
      if (
        scheduledEnd.isBefore(firstDate) &&
        maxScheduledDate.isAfter(lastDate)
      ) {
        return { left: 0, width: 100 }
      }
      return null
    }

    // Determine left edge: fallback to 0% only if scheduledEnd is before range
    let left: number
    if (startPos) {
      left = startPos.x + startPos.width
    } else if (scheduledEnd.isBefore(firstDate)) {
      left = 0
    } else {
      return null // scheduledEnd is after range: overrun not visible
    }

    // Determine right edge: fallback to 100% only if maxScheduledDate is after range
    let right: number
    if (endPos) {
      right = endPos.x + endPos.width
    } else if (maxScheduledDate.isAfter(lastDate)) {
      right = 100
    } else {
      return null // maxScheduledDate is before range: overrun not visible
    }

    const width = right - left
    if (width <= 0) return null

    return { left, width }
  }, [datePositions, scheduledEndDate, maxScheduledEndDate])
}

const useGridLines = (
  datePositions: DatePosition[],
  unit: GanttDisplayUnit,
  displayTimeScale: CalendarDateVO[]
): React.ReactNode[] => {
  return useMemo(() => {
    const isDayUnit = unit === GanttDisplayUnit.DAY
    const isMonthOrQuarter = GanttParameterVO.isMonthOrQuarterUnit(unit)
    const isWeekUnit = unit === GanttDisplayUnit.WEEK

    const gridLines: React.ReactNode[] = []

    for (let i = 1; i < datePositions.length; i++) {
      const { x, date } = datePositions[i]
      const matchScale =
        isDayUnit || displayTimeScale.some(v => v.isEqual(date))

      if (!matchScale) continue

      const isFirstday = !isMonthOrQuarter && date.isFirstday()
      const isFirstmonth = isMonthOrQuarter && date.isFirstmonth()
      const isFirstweek = isWeekUnit && date.isFirstweek()
      const isStrong = isFirstday || isFirstmonth || isFirstweek

      gridLines.push(
        <line
          key={`grid-${i}`}
          x1={`${x}%`}
          y1="0"
          x2={`${x}%`}
          y2="100%"
          stroke={isStrong ? Colors.GRID_LINE : Colors.GRID_LINE_LIGHT}
          strokeWidth="1"
        />
      )
    }
    return gridLines
  }, [datePositions, unit, displayTimeScale])
}

const useTodayLine = (
  datePositions: DatePosition[],
  today: CalendarDateVO,
  dim?: boolean
): React.ReactNode | null => {
  return useMemo(() => {
    const todayPos = datePositions.find(p => p.date.isEqual(today))
    if (!todayPos) return null
    const todayX = todayPos.x + todayPos.width / 2
    return (
      <line
        key="today-line"
        x1={`${todayX}%`}
        y1="0"
        x2={`${todayX}%`}
        y2="100%"
        stroke={dim ? colorPalette.monotone[3] : Colors.TODAY_BORDER}
        strokeWidth="2"
        strokeDasharray={dim ? '4 3' : undefined}
      />
    )
  }, [datePositions, today, dim])
}

function getActualBarColors(
  isDone: boolean,
  isLate: boolean,
  isV2: boolean
): { fill: string; border: string } {
  if (isDone) {
    return isV2
      ? { fill: Colors.ACTUAL_DONE, border: Colors.ACTUAL_DONE_BORDER }
      : { fill: Colors.DONE, border: Colors.DONE_BORDER }
  }
  if (isLate) {
    return isV2
      ? { fill: Colors.ACTUAL_LATE, border: Colors.ACTUAL_LATE_BORDER }
      : { fill: Colors.LATE, border: Colors.LATE_BORDER }
  }
  return isV2
    ? { fill: Colors.ACTUAL_NORMAL, border: Colors.ACTUAL_NORMAL_BORDER }
    : { fill: Colors.SCHEDULED, border: Colors.SCHEDULED_BORDER }
}

const useBars = (
  datePositions: DatePosition[],
  dateInfo: {
    wbsIsDoneOrDiscard: boolean
    wbsIsReview: boolean
    scheduledStart?: CalendarDateVO
    scheduledEnd?: CalendarDateVO
    actualStart?: CalendarDateVO
    actualEnd?: CalendarDateVO
    endOfToday: CalendarDateVO
  },
  isV2: boolean | undefined,
  visualType: GanttBarVisualType
): React.ReactNode[] => {
  return useMemo(() => {
    const {
      wbsIsDoneOrDiscard,
      wbsIsReview,
      scheduledStart,
      scheduledEnd,
      actualStart,
      actualEnd,
      endOfToday,
    } = dateInfo

    const bars: React.ReactNode[] = []

    const isSummary = isGanttSummaryVisualType(visualType)
    const barHeight = getGanttBarHeight(visualType)
    const isDeliverable = visualType === 'deliverable'
    const v2BarOffsetPx =
      barHeight / 2 + (isDeliverable ? ACTUAL_BAR_GAP / 2 : ACTUAL_BAR_GAP)

    // Legacy: percentage-based sizing
    const legacyBarY = 25
    const legacyBarHeight = 50

    const strokeWidth = isV2 ? 1 : 2
    const rx = isV2 ? 2 : 4

    const findPosition = (targetDate: CalendarDateVO) =>
      datePositions.find(p => p.date.isEqual(targetDate))

    const skipActualBars = !!(isV2 && isSummary)

    // Late bar
    if (
      !skipActualBars &&
      !actualStart &&
      !actualEnd &&
      scheduledStart?.isBefore(endOfToday)
    ) {
      const startPos = findPosition(scheduledStart)
      const endPos = findPosition(endOfToday)
      if (startPos && endPos && endPos.x > startPos.x) {
        const lateColor = isV2 ? Colors.ACTUAL_LATE : Colors.LATE
        const lateBorder = isV2 ? Colors.ACTUAL_LATE_BORDER : Colors.LATE_BORDER
        bars.push(
          <rect
            key="late-bar"
            x={`${startPos.x}%`}
            y={isV2 ? '50%' : `${legacyBarY}%`}
            width={`${endPos.x - startPos.x}%`}
            height={isV2 ? ACTUAL_BAR_HEIGHT : `${legacyBarHeight}%`}
            transform={isV2 ? `translate(0, ${v2BarOffsetPx})` : undefined}
            fill={lateColor}
            stroke={lateBorder}
            strokeWidth={strokeWidth}
            rx={rx}
          />
        )
      }
    }

    // Actual result bar
    if (!skipActualBars && actualStart) {
      const startPos = findPosition(actualStart)
      if (startPos) {
        const displayEnd = actualEnd || endOfToday
        const endPos = findPosition(displayEnd)
        const barEndX = endPos
          ? actualEnd
            ? endPos.x + endPos.width
            : endPos.x
          : 100
        const barWidth = barEndX - startPos.x

        if (barWidth > 0) {
          const isLate = scheduledEnd && displayEnd.isAfter(scheduledEnd)
          const isDone = wbsIsDoneOrDiscard || (wbsIsReview && actualEnd)
          const { fill: fillColor, border: borderColor } = getActualBarColors(
            !!isDone,
            !!isLate,
            !!isV2
          )

          bars.push(
            <rect
              key="actual-bar"
              x={`${startPos.x}%`}
              y={isV2 ? '50%' : `${legacyBarY}%`}
              width={`${barWidth}%`}
              height={isV2 ? ACTUAL_BAR_HEIGHT : `${legacyBarHeight}%`}
              transform={isV2 ? `translate(0, ${v2BarOffsetPx})` : undefined}
              fill={fillColor}
              stroke={borderColor}
              strokeWidth={strokeWidth}
              rx={rx}
            />
          )
        }
      }
    }

    return bars
  }, [datePositions, dateInfo, isV2, visualType])
}

const useMilestoneLines = (
  datePositions: DatePosition[],
  milestones?: MilestoneDetail[]
): React.ReactNode[] => {
  return useMemo(() => {
    if (!milestones || milestones.length === 0) return []

    const lines: React.ReactNode[] = []

    const findPosition = (targetDate: CalendarDateVO) =>
      datePositions.find(p => p.date.isEqual(targetDate))

    const groupedByDate = new Map<string, MilestoneDetail[]>()
    for (const milestone of milestones) {
      const dateKey = milestone.milestoneDate
      if (!groupedByDate.has(dateKey)) {
        groupedByDate.set(dateKey, [])
      }
      groupedByDate.get(dateKey)!.push(milestone)
    }

    for (const [dateKey, groupMilestones] of groupedByDate) {
      const milestoneDate = CalendarDateVO.of(new Date(dateKey))
      const pos = findPosition(milestoneDate)

      if (pos) {
        const firstMilestone = groupMilestones[0]
        const color = firstMilestone.color
        const xPos = pos.x + pos.width / 2

        lines.push(
          <line
            key={`milestone-${dateKey}`}
            x1={`${xPos}%`}
            y1="0"
            x2={`${xPos}%`}
            y2="100%"
            stroke={color}
            strokeWidth="2"
          />
        )
      }
    }

    return lines
  }, [datePositions, milestones])
}
